import { useState, type FormEvent } from 'react';
import { Alert, ApiError, Button, Field, Input } from '@busbook/ui';
import { api } from '../lib/api.js';
import { useT } from '../i18n/index.js';

/**
 * Turns an invitation link into a working account (docs/10
 * `/admin/auth/accept-invite`). Shown when the URL carries `?token=`, before any
 * session exists.
 */
export function AcceptInvite({ token, onAccepted }: { token: string; onAccepted: () => void }) {
  const t = useT();
  const [form, setForm] = useState({ name: '', password: '' });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await api.request<{ tokens: { accessToken: string; refreshToken: string } }>(
        '/admin/auth/accept-invite',
        {
          method: 'POST',
          auth: false,
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ token, name: form.name.trim(), password: form.password }),
        },
      );
      api.setTokens(result.tokens);
      onAccepted();
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === 'INVITE_INVALID'
          ? 'This invitation has expired or has already been used. Ask your administrator for a new one.'
          : 'We could not set up your account. Try again.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="grid min-h-screen place-items-center px-6">
      <div className="w-full max-w-sm">
        <div className="mb-7 flex items-center gap-2">
          <span
            className="grid h-7 w-7 place-items-center rounded text-xs font-bold"
            style={{ background: 'var(--color-primary)', color: '#fff' }}
            aria-hidden
          >
            B
          </span>
          <span className="font-semibold tracking-tight">{t('brand.ops')}</span>
        </div>

        <h1 className="text-xl font-semibold tracking-tight">{t('ai.setUp')}</h1>
        <p className="mt-1 text-sm" style={{ color: 'var(--color-muted)' }}>
          {t('ai.setUpHint')}
        </p>

        <form onSubmit={submit} className="mt-6 flex flex-col gap-4">
          <Field label={t('ai.yourName')} htmlFor="name">
            <Input
              id="name"
              autoComplete="name"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </Field>
          <Field label={t('f.password')} htmlFor="password" hint={t('ai.min10')}>
            <Input
              id="password"
              type="password"
              autoComplete="new-password"
              minLength={10}
              required
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
            />
          </Field>
          {error && <Alert tone="error">{error}</Alert>}
          <Button type="submit" size="lg" block loading={busy}>
            {t('ai.setAndSignIn')}
          </Button>
        </form>
      </div>
    </main>
  );
}
